import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import * as XLSX from 'xlsx';

export default function LCOFeedbackReport() {
  const { tenant, user } = useAuth();
  const primary = tenant?.branding?.primary_color || '#1B6B3A';
  const [feedbacks, setFeedbacks] = useState([]);
  const [summary, setSummary] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filterSub, setFilterSub] = useState('');
  const [filterLCO, setFilterLCO] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterMeghala, setFilterMeghala] = useState('');
  const [filterIssueType, setFilterIssueType] = useState('');
  const [subdists, setSubdists] = useState([]);

  useEffect(()=>{
    api.get('/weekly/subdists').then(r=>setSubdists(r.data.subdists||[])).catch(()=>{});
  },[]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if(filterSub) params.sub_dist = filterSub;
      if(filterLCO) params.lco_code = filterLCO;
      if(filterMeghala) params.meghala = filterMeghala;
      if(filterIssueType) params.issue_type = filterIssueType;
      if(filterFrom) params.from_date = filterFrom;
      if(filterTo) params.to_date = filterTo;
      const r = await api.get('/lco-feedback/reports', { params });
      setFeedbacks(r.data.feedbacks || []);
      setSummary(r.data.summary || []);
      setTotal(r.data.total || 0);
    } catch(e) {}
    setLoading(false);
  };

  const resetLCO = async (lco_code) => {
    if(!window.confirm(`Reset ${lco_code} to allow resubmission?`)) return;
    try {
      await api.delete(`/lco-feedback/reset/${lco_code}`);
      loadReports();
    } catch(e) { alert('Reset failed'); }
  };

  const exportExcel = () => {
    if(!feedbacks.length) return;
    const wb = XLSX.utils.book_new();
    // Summary sheet
    const s1 = [
      ['KeralaVision Kannur — LCO Feedback Report'],
      [`Generated: ${new Date().toLocaleString('en-IN')}`],
      [],
      ['Sub-District Summary'],
      ['Sub-District', 'Responses', 'Total Active', 'Total Suspended', 'Total Inactive'],
      ...summary.map(s=>[s._id||'Unknown', s.count, s.total_active, s.total_suspended, s.total_churn])
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(s1);
    ws1['!cols'] = [{wch:15},{wch:12},{wch:14},{wch:16},{wch:14}];
    XLSX.utils.book_append_sheet(wb, ws1, 'Summary');
    // Detail sheet
    const s2 = [
      ['LCO Code','LCO Name','Sub-District','Meghala','Active','Suspended','Inactive',
       'Technical Issues','Customer Care','Digital TV / OTT','Marketing','Support Needed','Urgent Matters','Submitted At'],
      ...feedbacks.map(f=>[
        f.lco_code, f.lco_name, f.sub_dist_code, f.meghala||'',
        f.active_count, f.suspended_count, f.churn_count,
        f.technical_issues||'', f.customer_care_issues||'',
        f.digital_tv_issues||'', f.marketing_challenges||'',
        f.support_needed||'', f.urgent_matters||'',
        new Date(f.submitted_at).toLocaleString('en-IN')
      ])
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(s2);
    ws2['!cols'] = [{wch:12},{wch:20},{wch:12},{wch:15},{wch:8},{wch:10},{wch:10},
      {wch:40},{wch:40},{wch:40},{wch:40},{wch:40},{wch:40},{wch:20}];
    XLSX.utils.book_append_sheet(wb, ws2, 'All Responses');
    XLSX.writeFile(wb, `LCO-Feedback-Report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const fmtDate = d => d ? new Date(d).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';
  const inp = {padding:'9px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',fontSize:'13px',outline:'none'};

  return (
    <div style={{padding:'20px',maxWidth:'1100px',margin:'0 auto'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'20px',flexWrap:'wrap',gap:'12px'}}>
        <div>
          <h2 style={{fontSize:'20px',fontWeight:'800',color:'#0f172a',margin:0}}>📋 LCO Feedback Reports</h2>
          <p style={{color:'#64748b',fontSize:'13px',marginTop:'4px'}}>
            KeralaVision Kannur — Operator Feedback
            <a href="/lco-feedback" target="_blank"
              style={{marginLeft:'10px',color:primary,fontWeight:'600',fontSize:'12px',textDecoration:'none'}}>
              🔗 Open Form
            </a>
          </p>
        </div>
        <div style={{display:'flex',gap:'8px'}}>
          <button onClick={exportExcel} disabled={!feedbacks.length}
            style={{padding:'8px 16px',background:'#16a34a',color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'700',fontSize:'13px',opacity:feedbacks.length?1:0.5}}>
            📥 Export Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{background:'white',borderRadius:'12px',padding:'16px',boxShadow:'0 1px 4px rgba(0,0,0,0.06)',marginBottom:'16px'}}>
        <div style={{display:'flex',gap:'10px',flexWrap:'wrap',alignItems:'flex-end'}}>
          {user?.role!=='subdist_admin' && (
            <div>
              <div style={{fontSize:'11px',fontWeight:'700',color:'#94a3b8',marginBottom:'4px'}}>SUB-DISTRICT</div>
              <select value={filterSub} onChange={e=>setFilterSub(e.target.value)} style={{...inp,width:'140px'}}>
                <option value="">All</option>
                {subdists.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#94a3b8',marginBottom:'4px'}}>LCO CODE</div>
            <input value={filterLCO} onChange={e=>setFilterLCO(e.target.value.toUpperCase())}
              placeholder="KB13F008..." style={{...inp,width:'130px',textTransform:'uppercase'}}/>
          </div>
          <div>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#94a3b8',marginBottom:'4px'}}>FROM</div>
            <input type="date" value={filterFrom} onChange={e=>setFilterFrom(e.target.value)} style={{...inp}}/>
          </div>
          <div>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#94a3b8',marginBottom:'4px'}}>TO</div>
            <input type="date" value={filterTo} onChange={e=>setFilterTo(e.target.value)} style={{...inp}}/>
          </div>
          <div>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#94a3b8',marginBottom:'4px'}}>MEGHALA</div>
            <input value={filterMeghala} onChange={e=>setFilterMeghala(e.target.value)}
              placeholder="Area name..." style={{...inp,width:'130px'}}/>
          </div>
          <div>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#94a3b8',marginBottom:'4px'}}>ISSUE TYPE</div>
            <select value={filterIssueType} onChange={e=>setFilterIssueType(e.target.value)} style={{...inp,width:'180px'}}>
              <option value="">All Issues</option>
              <option value="technical_issues">🔧 Technical Issues</option>
              <option value="customer_care_issues">📞 Customer Care</option>
              <option value="digital_tv_issues">📺 Digital TV / KEE OTT</option>
              <option value="marketing_challenges">📈 Marketing Challenges</option>
              <option value="support_needed">🤝 Support Needed</option>
              <option value="urgent_matters">⚠️ Urgent Matters</option>
            </select>
          </div>
          <div style={{display:'flex',gap:'6px',alignItems:'flex-end'}}>
            {[['Today','today'],['This Week','week'],['This Month','month'],['All','']].map(([l,p])=>(
              <button key={p} onClick={()=>{
                const now=new Date();
                if(p==='today'){const d=now.toISOString().split('T')[0];setFilterFrom(d);setFilterTo(d);}
                else if(p==='week'){const d=new Date(now);d.setDate(d.getDate()-7);setFilterFrom(d.toISOString().split('T')[0]);setFilterTo(now.toISOString().split('T')[0]);}
                else if(p==='month'){const d=new Date(now);d.setDate(d.getDate()-30);setFilterFrom(d.toISOString().split('T')[0]);setFilterTo(now.toISOString().split('T')[0]);}
                else{setFilterFrom('');setFilterTo('');}
              }} style={{padding:'7px 12px',border:'1px solid #e2e8f0',borderRadius:'8px',cursor:'pointer',
                fontSize:'12px',fontWeight:'600',background:'#f8fafc',color:'#475569'}}>
              {l}
            </button>
            ))}
          </div>
          <button onClick={loadReports}
            style={{padding:'9px 20px',background:primary,color:'white',border:'none',borderRadius:'8px',cursor:'pointer',fontWeight:'700',fontSize:'13px'}}>
            🔍 Generate
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary.length>0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:'10px',marginBottom:'16px'}}>
          <div style={{background:'white',borderRadius:'10px',padding:'14px',boxShadow:'0 1px 4px rgba(0,0,0,0.06)',textAlign:'center'}}>
            <div style={{fontSize:'28px',fontWeight:'900',color:primary}}>{total}</div>
            <div style={{fontSize:'11px',color:'#64748b',marginTop:'2px'}}>Total Responses</div>
          </div>
          {summary.map(s=>(
            <div key={s._id} style={{background:'white',borderRadius:'10px',padding:'14px',boxShadow:'0 1px 4px rgba(0,0,0,0.06)',textAlign:'center'}}>
              <div style={{fontSize:'13px',fontWeight:'800',color:primary,marginBottom:'4px'}}>{s._id||'Unknown'}</div>
              <div style={{fontSize:'22px',fontWeight:'900',color:'#0f172a'}}>{s.count}</div>
              <div style={{fontSize:'10px',color:'#94a3b8'}}>responses</div>
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {loading && <div style={{textAlign:'center',padding:'60px',color:'#94a3b8'}}>Loading...</div>}
      {!loading && feedbacks.length===0 && total===0 && (
        <div style={{background:'white',borderRadius:'12px',padding:'60px',textAlign:'center',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
          <div style={{fontSize:'32px',marginBottom:'12px'}}>📋</div>
          <div style={{color:'#94a3b8',fontSize:'14px'}}>Click Generate to load feedback responses</div>
        </div>
      )}
      {!loading && feedbacks.length>0 && (
        <div style={{background:'white',borderRadius:'12px',overflow:'hidden',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
          <div style={{padding:'12px 16px',borderBottom:'1px solid #f1f5f9',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontWeight:'700',fontSize:'13px'}}>{feedbacks.length} responses</span>
          </div>
          {feedbacks.map((f,i)=>(
            <div key={f._id} style={{borderBottom:'1px solid #f8fafc'}}>
              {/* Collapsed row */}
              <div style={{padding:'14px 16px',display:'flex',gap:'12px',alignItems:'flex-start',
                background:i%2?'#fafafa':'white',cursor:'pointer'}}
                onClick={()=>setExpanded(expanded===f._id?null:f._id)}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap',marginBottom:'4px'}}>
                    <span style={{fontWeight:'800',fontSize:'14px',color:primary}}>{f.lco_code}</span>
                    <span style={{fontSize:'12px',color:'#475569',fontWeight:'600'}}>{f.lco_name}</span>
                    <span style={{background:'#f1f5f9',color:'#475569',borderRadius:'6px',padding:'1px 8px',fontSize:'11px'}}>{f.sub_dist_code||'—'}</span>
                    {f.meghala && <span style={{background:'#eff6ff',color:'#2563eb',borderRadius:'6px',padding:'1px 8px',fontSize:'11px'}}>📍 {f.meghala}</span>}
                  </div>
                  <div style={{display:'flex',gap:'12px',fontSize:'11px',color:'#94a3b8'}}>
                    <span>🟢 {f.active_count?.toLocaleString('en-IN')} Active</span>
                    <span>🟡 {f.suspended_count?.toLocaleString('en-IN')} Suspended</span>
                    <span>🔴 {f.churn_count?.toLocaleString('en-IN')} Inactive</span>
                    <span>🕐 {fmtDate(f.submitted_at)}</span>
                  </div>
                </div>
                <div style={{display:'flex',gap:'6px',flexShrink:0,alignItems:'center'}}>
                  {user?.role==='director'||user?.role==='dist_admin'||user?.role==='dist_head'||user?.role==='tenant_admin'||user?.role==='superadmin' ? (
                    <button onClick={e=>{e.stopPropagation();resetLCO(f.lco_code);}}
                      style={{padding:'4px 10px',background:'#fee2e2',color:'#dc2626',border:'none',
                        borderRadius:'6px',cursor:'pointer',fontSize:'11px',fontWeight:'700'}}>
                      ↺ Reset
                    </button>
                  ):null}
                  <span style={{color:'#94a3b8',fontSize:'16px'}}>{expanded===f._id?'▲':'▼'}</span>
                </div>
              </div>
              {/* Expanded detail */}
              {expanded===f._id && (
                <div style={{background:'#f8fafc',padding:'16px',borderTop:'1px solid #f1f5f9'}}>
                  {[
                    ['🔧 Broadband Technical Issues / ബ്രോഡ്ബാൻഡ് സാങ്കേതിക പ്രശ്നങ്ങൾ', f.technical_issues],
                    ['📞 Customer Care / കസ്റ്റമർ കെയർ', f.customer_care_issues],
                    ['📺 Digital TV / KEE OTT', f.digital_tv_issues],
                    ['📈 Marketing Challenges / മാർക്കറ്റിംഗ്', f.marketing_challenges],
                    ['🤝 Support Needed / ആവശ്യമായ പിന്തുണ', f.support_needed],
                    ['⚠️ Urgent Matters / അടിയന്തര വിഷയങ്ങൾ', f.urgent_matters],
                  ].map(([label, value])=> value ? (
                    <div key={label} style={{marginBottom:'12px'}}>
                      <div style={{fontSize:'11px',fontWeight:'700',color:'#64748b',marginBottom:'4px'}}>{label}</div>
                      <div style={{fontSize:'13px',color:'#0f172a',background:'white',padding:'10px 12px',
                        borderRadius:'8px',border:'1px solid #e2e8f0',lineHeight:'1.6',whiteSpace:'pre-wrap'}}>
                        {value}
                      </div>
                    </div>
                  ) : null)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
